<template>
  <section class="postcode-search" :style="computedStyleObj">
    <label>Enter a postcode or street address *</label>
    <div class="search-wrapper" :class="{ 'search-loading': isSearching }">
      <input
        v-model="inputText"
        ref="search"
        type="text"
        :class="[{ 'has-errors': hasErrors }]"
      />
      <span v-if="hasErrors" class="validation-label">
        This field is required
      </span>
    </div>
    <ul
      ref="itemsList"
      class="postcode-search__initial postcode-search__list postcode-search-list"
    >
      <li v-for="item in items" :key="item.Id">
        <button @click="getLookup(item)">
          {{ item.Text }} - {{ item.Description }}
        </button>
      </li>
    </ul>
    <ol
      ref="addressList"
      class="postcode-search__address postcode-search__list postcode-search-list"
    >
      <li v-for="item in addresses" :key="item.Id">
        <button @click="getLookup(item)">
          {{ item.Text }} - {{ item.Description }}
        </button>
      </li>
    </ol>
  </section>
</template>

<script>
import { ref } from "@vue/composition-api";
import { registrationConfig } from "../helpers/config";

export default {
  props: ["index", "hasErrors"],
  components: {},
  data: () => {
    return {
      debouncedInput: "",
      items: [],
      addresses: [],
      address: {},
      timeout: null,
      compTimer: null,
      loqateApiKey: registrationConfig.loqateApiKey,
    };
  },
  setup(props, { emit }) {
    const isSearching = ref(false);
    emit("disableNext");

    return {
      isSearching,
    };
  },
  computed: {
    inputText: {
      get() {
        return this.debouncedInput;
      },
      set(val) {
        if (this.timeout) clearTimeout(this.timeout);
        this.timeout = setTimeout(() => {
          this.debouncedInput = val;
          if (val !== "") {
            this.getAddressData();
          }
        }, 500);
      },
    },
    computedStyleObj: function() {
      if (
        this.$refs["addressList"] === undefined &&
        this.$refs["itemsList"] === undefined
      ) {
        return {};
      } else if (this.addresses.length > 0) {
        return {
          height: this.$refs["addressList"].clientHeight + "px",
        };
      } else if (this.items.length > 0) {
        return {
          height: this.$refs["itemsList"].clientHeight + "px",
        };
      } else {
        return {};
      }
    },
  },
  methods: {
    async getAddressData() {
      this.isSearching = true;
      this.items = [];
      this.addresses = [];
      let resp = await fetch(
        `https://api.addressy.com/Capture/Interactive/Find/v1.10/json3.ws?Key=${this.loqateApiKey}&Text=${this.inputText}&IsMiddleware=False&Origin=&Countries=&Limit=10&Language=&Bias=&Filters=`,
        {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        },
      );
      let items = await resp.json();
      this.items = items.Items;
      this.isSearching = false;
    },
    async getLookup(item) {
      // Loqate requires you to continue fetching using the Find endpoint until you reach an address.
      if (item.Type !== "Address") {
        let resp = await fetch(
          `https://api.addressy.com/Capture/Interactive/Find/v1.10/json3.ws?Key=${this.loqateApiKey}&Text=${this.inputText}&Container=${item.Id}&IsMiddleware=False&Origin=&Countries=&Limit=10&Language=&Bias=&Filters=`,
          {
            headers: {
              Accept: "application/json",
              "Content-Type": "application/json",
            },
          },
        );
        let items = await resp.json();
        this.addresses = items.Items;
      } else {
        let resp = await fetch(
          `https://api.addressy.com/Capture/Interactive/Retrieve/v1.00/json3.ws?Key=${this.loqateApiKey}&Id=${item.Id}&IsMiddleware=False&Origin=&Countries=&Limit=10&Language=&Bias=&Filters=`,
          {
            headers: {
              Accept: "application/json",
              "Content-Type": "application/json",
            },
          },
        );
        let addressItem = await resp.json();
        if (addressItem.Items.length > 0) {
          this.address = addressItem.Items[0];
          this.$emit("place-change", this.address);
          this.addresses = [];
          this.items = [];
          this.debouncedInput = "";
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../styles/global-variables";

.has-errors {
  border-color: var(--error);
}

.validation-label {
  margin: {
    top: -0.5em;
    margin-bottom: 1em;
  }
}

hr {
  margin-bottom: 32px;
}

label {
  display: block;
  font-size: 14px;
  line-height: 16px;
  margin-bottom: 16px;
}

input {
  background-color: var(--white);
  border: 1px solid var(--mid-grey);
  border-radius: 3px;
  box-sizing: border-box;
  margin-bottom: 16px;
  padding: 10px 48px 10px 16px;
  position: relative;
  width: 100%;
  z-index: 1;

  &:focus {
    border: 1px solid var(--primary);
  }
}

@keyframes spinner {
  to {
    transform: rotate(360deg);
  }
}

.search-wrapper {
  position: relative;

  &.search-loading:after {
    opacity: 1;
  }

  &:after {
    opacity: 0;
    content: "";
    box-sizing: border-box;
    position: absolute;
    top: 50%;
    right: 16px;
    width: 20px;
    height: 20px;
    margin-top: -20px;
    margin-left: -10px;
    border-radius: 50%;
    border: 2px solid #fff;
    border-top-color: var(--primary);
    z-index: 1;
    animation: spinner 0.6s linear infinite;
  }
}

.postcode-search {
  height: 100%;
  position: relative;
  transition: height 0.2s ease;
  will-change: height;

  &.postcode-search--expand {
    height: 200px;
  }
}

.postcode-search-list {
  background-color: var(--white);
  list-style: none;
  margin: 0;
  max-height: 190px;
  padding: 0px;
  overflow-y: auto;
  position: absolute;
  top: calc(100% - 16px);
  z-index: 0;
  width: calc(100% - 2px);
  border-top: 0px;

  &:not(:empty) {
    border: 1px solid var(--mid-grey);
    border-bottom-right-radius: 4px;
    border-bottom-left-radius: 4px;
  }

  li {
    border-bottom: 1px solid var(--mid-grey);
    padding: 12px 8px;
    line-height: 11px;
    margin-bottom: 0;
    &:hover {
      background-color: var(--light-grey);
      cursor: pointer;
    }
  }

  button {
    appearance: none;
    border: none;
    background: transparent;
    color: var(--body);
    font-size: 11px;
    height: 100%;
    padding: 0;
    text-align: left;
    width: 100%;
    &:hover {
      cursor: pointer;
    }
  }
}
</style>
